import { useMemo, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { ApiError } from "@/lib/api/unwrap";
import { Button } from "@/components/ui/button";
import { DirectoryPanelShell } from "@/components/directory/DirectoryPanelShell";
import { FacetGroup } from "@/components/directory/FacetGroup";
import { FavoritesToggle } from "@/components/directory/FavoritesToggle";
import { toast } from "@/components/ui/sonner";
import { useCompanySizes } from "@/features/questionnaire/api";

import { ToolCard } from "./ToolCard";
import {
  useAddToolFavorite,
  useRemoveToolFavorite,
  useToolDirectoryQuery,
  useToolDirectorySectorsQuery,
  useToolFavoritesQuery,
} from "../../api/hooks";
import type { DirectoryTool, ToolType } from "../../types";
import { EmptyState, ErrorState, Loading } from "../ui/states";

/** Tools shown before "see more" reveals the tail (OAC-440: max 5 + "+"). */
const INITIAL_COUNT = 5;

/** Reference option shape shared by the sector and company-size facets. */
interface RefOption {
  id: string;
  name: string;
}

/** EnterpriseProfile.EnterpriseType — the choices the model's array field uses. */
const ORG_TYPES = [
  "self_employed",
  "private",
  "cooperative",
  "not_for_profit",
  "economical_development",
  "municipality",
  "other",
] as const;

const TOOL_TYPES: ToolType[] = [
  "guide",
  "action_sheet",
  "video",
  "analysis_grid",
  "other",
];

function isApiError(err: unknown): err is ApiError {
  return typeof err === "object" && err !== null && "status" in err;
}

/**
 * The refine rail: search + tool type + the harmonized facets (secteurs
 * associés, taille des entreprises, types d'organisation) + favourites toggle
 * — parity with the Django ``repertoire/_filters.html``. The facets are applied
 * server-side so both apps narrow through one query; search and favourites-only
 * stay client-side.
 *
 * No region facet, unlike financial aids: a guide isn't restricted by territory.
 */
function ToolFilters({
  search,
  onSearch,
  selectedTypes,
  onToggleType,
  sectors,
  selectedSectors,
  onToggleSector,
  companySizes,
  selectedCompanySizes,
  onToggleCompanySize,
  selectedOrgTypes,
  onToggleOrgType,
  favoriteCount,
  favoritesOnly,
  onFavoritesOnly,
  showFavorites = true,
}: {
  search: string;
  onSearch: (v: string) => void;
  selectedTypes: ToolType[];
  onToggleType: (type: ToolType) => void;
  sectors: RefOption[];
  selectedSectors: string[];
  onToggleSector: (id: string) => void;
  companySizes: RefOption[];
  selectedCompanySizes: string[];
  onToggleCompanySize: (id: string) => void;
  selectedOrgTypes: string[];
  onToggleOrgType: (value: string) => void;
  favoriteCount: number;
  favoritesOnly: boolean;
  onFavoritesOnly: (v: boolean) => void;
  /** False when the caller renders the favourites toggle itself (panel chrome). */
  showFavorites?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <input
        type="search"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder={t("tools.filters.search")}
        aria-label={t("tools.filters.search")}
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
      />
      {showFavorites && (
        <FavoritesToggle
          count={favoriteCount}
          on={favoritesOnly}
          onChange={onFavoritesOnly}
          label={t("tools.filters.favoritesOnly")}
          className="w-full"
        />
      )}
      <div className="overflow-hidden rounded-lg border border-border">
        <FacetGroup
          label={t("tools.filters.type")}
          options={TOOL_TYPES.map((type) => ({
            value: type,
            label: t(`tools.types.${type}`),
          }))}
          selected={selectedTypes}
          onToggle={(v) => onToggleType(v as ToolType)}
          defaultOpen
        />
        {sectors.length > 0 && (
          <FacetGroup
            label={t("tools.filters.sectorsServed")}
            options={sectors.map((x) => ({ value: x.id, label: x.name }))}
            selected={selectedSectors}
            onToggle={onToggleSector}
          />
        )}
        {companySizes.length > 0 && (
          <FacetGroup
            label={t("tools.filters.companySizes")}
            options={companySizes.map((x) => ({ value: x.id, label: x.name }))}
            selected={selectedCompanySizes}
            onToggle={onToggleCompanySize}
          />
        )}
        <FacetGroup
          label={t("tools.filters.orgTypes")}
          options={ORG_TYPES.map((value) => ({
            value,
            label: t(`questionnaire.enterpriseTypes.${value}`),
          }))}
          selected={selectedOrgTypes}
          onToggle={onToggleOrgType}
        />
      </div>
    </div>
  );
}

export function DirectoryView({
  action,
  favoritesDefault = false,
  variant = "page",
  panelFooterAction,
}: {
  action: string;
  favoritesDefault?: boolean;
  variant?: "page" | "panel";
  /** Escape hatch rendered in the panel footer (ignored on the full page). */
  panelFooterAction?: ReactNode;
}) {
  const { t } = useTranslation();
  const [favoritesOnly, setFavoritesOnly] = useState(favoritesDefault);
  const [showAll, setShowAll] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<ToolType[]>([]);
  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
  const [selectedCompanySizes, setSelectedCompanySizes] = useState<string[]>([]);
  const [selectedOrgTypes, setSelectedOrgTypes] = useState<string[]>([]);

  // Facets are applied server-side (parity with the Django directory); search
  // and favourites-only stay client-side, both derivable from the card payload.
  const serverFilters = useMemo(
    () => ({
      sectors_served: selectedSectors,
      company_sizes_served: selectedCompanySizes,
      organization_types_served: selectedOrgTypes,
    }),
    [selectedSectors, selectedCompanySizes, selectedOrgTypes],
  );

  const directory = useToolDirectoryQuery(action, serverFilters);
  const sectorsQuery = useToolDirectorySectorsQuery();
  const companySizesQuery = useCompanySizes();
  const favoritesQuery = useToolFavoritesQuery(action);
  const addFavorite = useAddToolFavorite();
  const removeFavorite = useRemoveToolFavorite();

  const favoriteIdByTool = useMemo(() => {
    const map = new Map<string, string>();
    for (const fav of favoritesQuery.data ?? []) map.set(fav.tool, fav.id);
    return map;
  }, [favoritesQuery.data]);

  const handleToggleFavorite = async (tool: DirectoryTool) => {
    if (!action) return;
    setTogglingId(tool.id);
    try {
      if (tool.is_favorite) {
        const favId = favoriteIdByTool.get(tool.id);
        if (favId) await removeFavorite.mutateAsync(favId);
      } else {
        await addFavorite.mutateAsync({ action, toolId: tool.id });
      }
    } catch (err) {
      const detail = isApiError(err) ? err.detail : undefined;
      toast.error(detail ?? t("tools.error"));
    } finally {
      setTogglingId(null);
    }
  };

  const toggleType = (type: ToolType) => {
    setShowAll(false);
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((x) => x !== type) : [...prev, type],
    );
  };
  const toggleSector = (id: string) => {
    setShowAll(false);
    setSelectedSectors((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  const toggleCompanySize = (id: string) => {
    setShowAll(false);
    setSelectedCompanySizes((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  const toggleOrgType = (value: string) => {
    setShowAll(false);
    setSelectedOrgTypes((prev) =>
      prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value],
    );
  };

  if (directory.isPending) return <Loading />;

  if (directory.isError) {
    const status = isApiError(directory.error) ? directory.error.status : 0;
    const message =
      status === 404
        ? t("tools.directory.unavailable")
        : isApiError(directory.error)
          ? directory.error.detail
          : undefined;
    const onRetry =
      status >= 400 && status < 500 ? undefined : () => void directory.refetch();
    return <ErrorState message={message} onRetry={onRetry} />;
  }

  const query = search.trim().toLowerCase();
  // Everything the current facets admit, favourites aside. `directory.data` is
  // already narrowed server-side by the sector / size / org-type facets.
  const matching = directory.data.filter((tl) => {
    if (selectedTypes.length && !selectedTypes.includes(tl.tool_type)) return false;
    if (
      query &&
      !`${tl.title} ${tl.author}`.toLowerCase().includes(query)
    )
      return false;
    return true;
  });
  const tools = favoritesOnly ? matching.filter((tl) => tl.is_favorite) : matching;
  const shown = showAll || favoritesOnly ? tools : tools.slice(0, INITIAL_COUNT);
  const extraCount = favoritesOnly
    ? 0
    : tools.length - Math.min(tools.length, INITIAL_COUNT);
  // What pressing the ♥ would actually show. The favourites query is unfaceted,
  // so using its length let the badge advertise a count whose list then rendered
  // the "no favourite" empty state.
  const favoriteCount = matching.filter((tl) => tl.is_favorite).length;
  const hasActiveFilters =
    Boolean(query) ||
    selectedTypes.length > 0 ||
    selectedSectors.length > 0 ||
    selectedCompanySizes.length > 0 ||
    selectedOrgTypes.length > 0;

  const countLabel = favoritesOnly
    ? t("tools.directory.favoriteCount", { count: tools.length })
    : t("tools.directory.resultCount", { count: tools.length });

  const isPanel = variant === "panel";
  const cardGridClass = isPanel
    ? "grid gap-4"
    : "grid gap-4 sm:grid-cols-2 xl:grid-cols-3";

  const setFavoritesOnlyAndReset = (value: boolean) => {
    setFavoritesOnly(value);
    setShowAll(false);
  };

  const filtersEl = (
    <ToolFilters
      search={search}
      onSearch={(v) => {
        setSearch(v);
        setShowAll(false);
      }}
      selectedTypes={selectedTypes}
      onToggleType={toggleType}
      sectors={sectorsQuery.data ?? []}
      selectedSectors={selectedSectors}
      onToggleSector={toggleSector}
      companySizes={companySizesQuery.data ?? []}
      selectedCompanySizes={selectedCompanySizes}
      onToggleCompanySize={toggleCompanySize}
      selectedOrgTypes={selectedOrgTypes}
      onToggleOrgType={toggleOrgType}
      favoriteCount={favoriteCount}
      favoritesOnly={favoritesOnly}
      onFavoritesOnly={setFavoritesOnlyAndReset}
      // In the panel the ♥ lives in the always-visible toolbar row, so the rail
      // must not render a second copy of the same control.
      showFavorites={!isPanel}
    />
  );

  const resultsBody =
    tools.length === 0 ? (
      <EmptyState
        title={
          favoritesOnly
            ? t("tools.directory.noFavorites")
            : hasActiveFilters
              ? t("tools.directory.noneFiltered")
              : t("tools.directory.none")
        }
      >
        {favoritesOnly && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setFavoritesOnly(false);
              setShowAll(false);
            }}
          >
            {t("tools.directory.seeAll")}
          </Button>
        )}
      </EmptyState>
    ) : (
      <>
        <div className={cardGridClass}>
          {shown.map((tool) => (
            <ToolCard
              key={tool.id}
              tool={tool}
              onToggleFavorite={(tl) => void handleToggleFavorite(tl)}
              isToggling={togglingId === tool.id}
            />
          ))}
        </div>
        {!showAll && extraCount > 0 && (
          <div className="pt-2 text-center">
            <Button
              variant="outline"
              onClick={() => setShowAll(true)}
              className="gap-2"
            >
              {t("tools.directory.seeMore", { count: extraCount })}
              <ChevronDown className="size-4" aria-hidden />
            </Button>
          </div>
        )}
      </>
    );

  // Panel chrome: favourites lead the toolbar row, the count trails in the
  // footer alongside the full-directory link.
  if (isPanel) {
    return (
      <DirectoryPanelShell
        filtersLabel={t("tools.filters.heading")}
        filtersOpenByDefault={hasActiveFilters}
        filters={filtersEl}
        favorites={{
          count: favoriteCount,
          on: favoritesOnly,
          onChange: setFavoritesOnlyAndReset,
          label: t("tools.filters.favoritesOnly"),
        }}
        countLabel={countLabel}
        footerAction={panelFooterAction}
      >
        {resultsBody}
      </DirectoryPanelShell>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[17rem_1fr]">
      {/* Sticky and taller than the viewport, the rail only unpins at the bottom of
          its grid row — with several facets open, reaching the last groups meant
          scrolling the page past every result. Let the rail scroll on its own. */}
      <aside className="lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:self-start lg:overflow-y-auto">
        {filtersEl}
      </aside>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{countLabel}</p>
        {resultsBody}
      </div>
    </div>
  );
}
